import { useState, useCallback, useRef, useEffect } from 'react';
import Vapi from '@vapi-ai/web';
import { WEBHOOK_CREATE_DEMO_ASSISTANT, WEBHOOK_CLEANUP_DEMO_ASSISTANT } from '@/lib/constants';

export type DemoMode = 'inbound' | 'outbound';
export type CallStatus =
  | 'idle'
  | 'requesting-mic'
  | 'generating'
  | 'connecting'
  | 'active'
  | 'ending'
  | 'ended'
  | 'error';

// Business data extracted from Google Maps search
export interface GoogleMapsBusinessData {
  place_id?: string;
  name?: string;
  address?: string;
  phone?: string;
  website?: string;
  rating?: number;
  reviews_count?: number;
  hours_of_operation?: string[];
  business_type?: string;
}

export interface DemoConfig {
  businessName: string;
  city: string; // Required for weather-aware emergency triage
  website?: string; // Optional - user can provide their website
  services?: string[]; // Optional - will be inferred from Google Maps
  industry?: string; // Industry type for showcase demo (hvac, plumbing, electrical, etc.)
  demoMode: DemoMode;
  // Google Maps extracted data (populated after search)
  googleMapsData?: GoogleMapsBusinessData;
  // Voice selection - real 11labs voice IDs (see SettingsAIVoice.tsx VOICE_OPTIONS)
  // Default: Sarah (EXAVITQu4vr4xnSDxMaL)
  voiceId?: string;
}

export interface TranscriptEntry {
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
  isFinal: boolean;
}

export interface RateLimitInfo {
  message: string;
  remainingDemos: number;
  maxAllowed: number;
}

export interface UseVapiDemoReturn {
  // State
  status: CallStatus;
  duration: number;
  transcript: TranscriptEntry[];
  isMuted: boolean;
  volumeLevel: number;
  error: string | null;
  remainingDemos: number | null;
  isRateLimited: boolean;

  // Actions
  startDemo: (config: DemoConfig, sessionId: string) => Promise<void>;
  endDemo: () => void;
  toggleMute: () => void;
  resetDemo: () => void;

  // Status
  isSupported: boolean;
}

const MAX_CALL_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const VAPI_PUBLIC_KEY = import.meta.env.VITE_VAPI_PUBLIC_KEY;

// Validate VAPI key format (should be a UUID-like string, typically 36 chars)
const isValidVapiKey = (key: string | undefined): boolean => {
  if (!key) return false;
  // Check for common placeholder patterns
  const placeholderPatterns = [
    'your-vapi-public-key-here',
    'your-vapi-key',
    'vapi-key',
    'xxx',
    'placeholder',
    'test',
  ];
  if (placeholderPatterns.some(p => key.toLowerCase().includes(p))) return false;
  // VAPI keys are typically UUID format (36 chars) or longer
  if (key.length < 30) return false;
  return true;
};

// Validate configuration on load
const CONFIG_ERRORS = {
  supabase: !SUPABASE_URL ? 'VITE_SUPABASE_URL is not configured' : null,
  vapi: !isValidVapiKey(VAPI_PUBLIC_KEY)
    ? 'VITE_VAPI_PUBLIC_KEY is not configured or invalid. Get your public key from https://vapi.ai/dashboard'
    : null,
};

export function useVapiDemo(): UseVapiDemoReturn {
  // State
  const [status, setStatus] = useState<CallStatus>('idle');
  const [duration, setDuration] = useState(0);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [isMuted, setIsMuted] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [remainingDemos, setRemainingDemos] = useState<number | null>(null);
  const [isRateLimited, setIsRateLimited] = useState(false);

  // Refs
  const vapiRef = useRef<Vapi | null>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const maxDurationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentAssistantIdRef = useRef<string | null>(null);
  const currentSessionIdRef = useRef<string | null>(null);
  const callStartTimeRef = useRef<Date | null>(null);

  // Check browser support
  const isSupported = typeof navigator !== 'undefined'
    && 'mediaDevices' in navigator
    && 'getUserMedia' in navigator.mediaDevices;

  // Cleanup call resources
  const cleanupCall = useCallback(async () => {
    // Clear timers
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    if (maxDurationTimeoutRef.current) {
      clearTimeout(maxDurationTimeoutRef.current);
      maxDurationTimeoutRef.current = null;
    }

    // Cleanup transient assistant on backend
    if (currentAssistantIdRef.current) {
      const cleanupController = new AbortController();
      const cleanupTimeout = setTimeout(() => cleanupController.abort(), 10000);
      try {
        await fetch(WEBHOOK_CLEANUP_DEMO_ASSISTANT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: cleanupController.signal,
          body: JSON.stringify({
            assistantId: currentAssistantIdRef.current,
            callDuration: duration,
            callStartTime: callStartTimeRef.current?.toISOString(),
            sessionId: currentSessionIdRef.current,
          }),
        });
      } catch (err) {
        if (import.meta.env.DEV) console.error('Failed to cleanup assistant:', err);
      } finally {
        clearTimeout(cleanupTimeout);
      }
      currentAssistantIdRef.current = null;
    }
  }, [duration]);

  // Initialize VAPI client
  const initializeVapi = useCallback(() => {
    if (!vapiRef.current && VAPI_PUBLIC_KEY) {
      vapiRef.current = new Vapi(VAPI_PUBLIC_KEY);

      // Set up event listeners
      vapiRef.current.on('call-start', () => {
        setStatus('active');
        callStartTimeRef.current = new Date();

        // Start duration timer
        durationIntervalRef.current = setInterval(() => {
          setDuration(d => d + 1);
        }, 1000);

        // Set max duration timeout
        maxDurationTimeoutRef.current = setTimeout(() => {
          if (import.meta.env.DEV) console.log('Max call duration reached, ending call');
          if (vapiRef.current) {
            vapiRef.current.stop();
          }
        }, MAX_CALL_DURATION_MS);
      });

      vapiRef.current.on('call-end', () => {
        setStatus('ended');
        cleanupCall();
      });

      vapiRef.current.on('speech-start', () => {
        // User started speaking - could add visual indicator
      });

      vapiRef.current.on('speech-end', () => {
        // User stopped speaking
      });

      vapiRef.current.on('volume-level', (level: number) => {
        setVolumeLevel(level);
      });

      vapiRef.current.on('message', (message: { type: string; role?: string; transcript?: string; transcriptType?: string }) => {
        if (message.type === 'transcript' && message.transcript) {
          const entry: TranscriptEntry = {
            role: message.role as 'user' | 'assistant',
            text: message.transcript,
            timestamp: new Date(),
            isFinal: message.transcriptType === 'final',
          };

          setTranscript(prev => {
            // Replace partial transcripts with final ones
            if (entry.isFinal) {
              const filtered = prev.filter(
                t => t.role !== entry.role || t.isFinal
              );
              return [...filtered, entry];
            }
            return [...prev, entry];
          });
        }
      });

      vapiRef.current.on('error', (err: Error) => {
        if (import.meta.env.DEV) console.error('VAPI Error:', err);
        setError(err.message || 'An error occurred during the call');
        setStatus('error');
        cleanupCall();
      });
    }

    return vapiRef.current;
  }, [cleanupCall]);

  // Request microphone permission
  const requestMicrophonePermission = useCallback(async (): Promise<boolean> => {
    setStatus('requesting-mic');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop tracks immediately - VAPI will request again
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (err) {
      const error = err as Error;

      if (error.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone access to use the voice demo.');
      } else if (error.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else if (error.name === 'NotReadableError') {
        setError('Microphone is in use by another application. Please close other apps using the mic.');
      } else {
        setError(`Microphone error: ${error.message}`);
      }

      setStatus('error');
      return false;
    }
  }, []);

  // Start demo call
  const startDemo = useCallback(async (config: DemoConfig, sessionId: string) => {
    setError(null);
    setTranscript([]);
    setDuration(0);
    setIsRateLimited(false);
    currentSessionIdRef.current = sessionId;

    // Check configuration first
    if (CONFIG_ERRORS.supabase) {
      setError(CONFIG_ERRORS.supabase);
      setStatus('error');
      return;
    }
    if (CONFIG_ERRORS.vapi) {
      setError(CONFIG_ERRORS.vapi);
      setStatus('error');
      return;
    }

    // Check browser support
    if (!isSupported) {
      setError('Your browser does not support voice calls. Please use Chrome, Firefox, or Safari.');
      setStatus('error');
      return;
    }

    // Request microphone permission first
    const hasPermission = await requestMicrophonePermission();
    if (!hasPermission) return;

    setStatus('generating');

    try {
      // 1. Create transient assistant via N8N workflow (with timeout + single retry)
      // Retry handles n8n known issue: 200 OK with empty body on internal errors
      const MAX_RETRIES = 1;
      const RETRY_DELAY_MS = 3000;
      const TIMEOUT_MS = 30000;
      let responseData: { assistantId?: string; remainingDemos?: number; sessionId?: string } | null = null;
      let lastError: Error | null = null;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (attempt > 0) {
          if (import.meta.env.DEV) console.log(`Retrying demo creation (attempt ${attempt + 1})...`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

        try {
          const response = await fetch(WEBHOOK_CREATE_DEMO_ASSISTANT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...config,
              sessionId,
            }),
            signal: controller.signal,
          });
          clearTimeout(timeoutId);

          if (!response.ok) {
            let data: { error?: string; message?: string } = {};
            try {
              const text = await response.text();
              if (text) {
                data = JSON.parse(text);
              }
            } catch {
              // Response wasn't JSON or was empty
            }

            // Rate limit — don't retry
            if (response.status === 429) {
              setError(data.message || 'You have reached the maximum number of demos for today. Please try again tomorrow.');
              setRemainingDemos(0);
              setIsRateLimited(true);
              setStatus('error');
              return;
            }

            // HTTP errors — don't retry (server explicitly rejected)
            throw new Error(data.error || data.message || `Server error: ${response.status} ${response.statusText}`);
          }

          // Parse response safely - handle empty or malformed JSON
          const text = await response.text();
          if (!text || text.trim() === '') {
            // Empty response — retry (n8n known issue: 200 OK with empty body)
            lastError = new Error('Demo service returned an empty response.');
            if (attempt < MAX_RETRIES) continue;
            throw lastError;
          }

          try {
            responseData = JSON.parse(text);
          } catch {
            throw new Error('Failed to process demo response. Please try again.');
          }

          // Success — break out of retry loop
          break;
        } catch (fetchError) {
          clearTimeout(timeoutId);

          if (fetchError instanceof DOMException && fetchError.name === 'AbortError') {
            lastError = new Error('Demo setup timed out. The service may be busy — please try again.');
            if (attempt < MAX_RETRIES) continue;
            throw lastError;
          }

          if (fetchError instanceof TypeError && (fetchError as TypeError).message.includes('fetch')) {
            lastError = new Error('Unable to connect to demo service. Please try again in a moment.');
            if (attempt < MAX_RETRIES) continue;
            throw lastError;
          }

          // Non-retryable errors — throw immediately
          throw fetchError;
        }
      }

      if (!responseData) {
        throw lastError || new Error('Failed to create demo after retries. Please try again.');
      }

      const { assistantId, remainingDemos: remaining } = responseData;

      // Validate we got a valid assistant ID
      if (!assistantId) {
        throw new Error('Demo service did not return a valid assistant. Please try again.');
      }

      currentAssistantIdRef.current = assistantId;
      setRemainingDemos(remaining ?? null);

      // 2. Initialize VAPI and start call
      setStatus('connecting');
      const vapi = initializeVapi();

      if (!vapi) {
        throw new Error('Failed to initialize VAPI client. Please check your configuration.');
      }

      // Start the call
      await vapi.start(assistantId);

    } catch (err) {
      const error = err as Error;
      if (import.meta.env.DEV) console.error('Failed to start demo:', error);
      setError(error.message || 'Failed to start demo call');
      setStatus('error');
      cleanupCall();
    }
  }, [isSupported, requestMicrophonePermission, initializeVapi, cleanupCall]);

  // End demo call
  const endDemo = useCallback(() => {
    if (vapiRef.current) {
      setStatus('ending');
      vapiRef.current.stop();
    }
  }, []);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (vapiRef.current) {
      const newMuted = !isMuted;
      vapiRef.current.setMuted(newMuted);
      setIsMuted(newMuted);
    }
  }, [isMuted]);

  // Reset demo state
  const resetDemo = useCallback(() => {
    setStatus('idle');
    setDuration(0);
    setTranscript([]);
    setIsMuted(false);
    setVolumeLevel(0);
    setError(null);
    setIsRateLimited(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      if (maxDurationTimeoutRef.current) {
        clearTimeout(maxDurationTimeoutRef.current);
      }
      if (vapiRef.current) {
        vapiRef.current.stop();
      }
    };
  }, []);

  return {
    status,
    duration,
    transcript,
    isMuted,
    volumeLevel,
    error,
    remainingDemos,
    isRateLimited,
    startDemo,
    endDemo,
    toggleMute,
    resetDemo,
    isSupported,
  };
}
